import { Component, OnInit, ViewChild } from "@angular/core";
import { Router } from "@angular/router";
import { FeatureViews } from "src/app/ui/models/domains/features/custom-feature-views";
import { PageViews } from "src/app/ui/models/domains/features/custom-page-views";
import { MaskInput } from "src/app/ui/models/domains/masks/mask.model";
import { ProductLabelInfo } from "src/app/ui/models/domains/pbm/product-management/product-label-info";
import { UserPermissions } from "src/app/ui/models/domains/pbm/user/user-permissions";
import { RoutePath } from "src/app/ui/models/domains/route-paths.model";
import { FeatureViewService } from "src/app/ui/services/feature-view.service";
import { LoadingService } from "src/app/ui/services/loading.service";
import { ProductManagementService } from "src/app/ui/services/pbm/product-management.service";
import { UserService } from "src/app/ui/services/user.service";
import { ProductManagementSearchRequest } from "./product-management-search-request";
import { ProductManagementSearchResult } from "./product-management-search-result";
import moment from 'moment';
import { Toast } from "../../../toast/toast";
import { MatPaginator, PageEvent } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";

@Component({
  selector: "search-product",
  templateUrl: "./search-product.component.html",
  styleUrls: ["./search-product.component.scss"],
})
export class SearchProductComponent implements OnInit {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  public displayedColumns: string[] = ["ean", "name", "presentation", "laboratory", "productType", "productSubType", "modified", "edit"];
  private productManagementSearchResult: ProductManagementSearchResult;
  public filterText: string;

  public pendingProducts: boolean;
  public startAt: string;
  public endAt: string;
  public dateMaskInput = MaskInput.getDateMask();

  constructor(
    private featureViewService: FeatureViewService,
    private productManagementService: ProductManagementService,
    private loadingService: LoadingService,
    private router: Router,
    private userService: UserService,
    private toast: Toast,
  ) {}

  async ngOnInit() {
    this.featureViewService.activeFeatureView(FeatureViews.SearchProduct, PageViews.ProductManagementPageView);
    const loadingRef = this.loadingService.beginLoading();
    try {
      let lastRequest = this.productManagementService.getLastRequest();
      let lastMatTablePage = 0;
      if (lastRequest) {
        this.filterText = lastRequest.getFilterText();
        this.pendingProducts = lastRequest.getPendingProducts();
        this.startAt = lastRequest.getStartAt();
        this.endAt = lastRequest.getEndAt();
        lastMatTablePage = (lastRequest.getStartIndex() - 1) / lastRequest.getTotalPerPages();
      } else {
        lastRequest = new ProductManagementSearchRequest();
      }
      this.productManagementSearchResult = await this.productManagementService.searchProducts(lastRequest);
      await this.checkPaginator();
      this.paginator.pageIndex = lastMatTablePage;
    } catch (er) {
      console.error(er);
    } finally {
      loadingRef.closeLoading();
    }
  }

  private async checkPaginator() {
    return new Promise((resolve) => {
      setInterval(() => {
        if (this.paginator) {
          resolve(true);
        }
      }, 1);
      setTimeout(() => {
        resolve(false);
      }, 5000);
    });
  }

  public getProducts(): ProductLabelInfo[] {
    return this.productManagementSearchResult && this.productManagementSearchResult.getProducts();
  }

  public getProductsLenth(): number {
    return this.productManagementSearchResult && this.productManagementSearchResult.getTotal();
  }

  public hasProducts(): boolean {
    return this.productManagementSearchResult && this.productManagementSearchResult.getProducts().length > 0;
  }

  public goToInsertProduct(): void {
    this.router.navigate([RoutePath.InsertProduct]);
  }

  public goEditProduct(element: ProductLabelInfo): void {
    this.productManagementService.setProduct(element);
    this.router.navigate([RoutePath.EditProduct]);
  }

  public async onFilterSearch() {
    if (this.filtersValidate()) {
      const loadingRef = this.loadingService.beginLoading();
      try {
        const request = new ProductManagementSearchRequest();
        request.setFilterText(this.filterText);
        request.setPendingProducts(this.pendingProducts);
        request.setStartAt(this.startAt);
        request.setEndAt(this.endAt);
        this.productManagementSearchResult = await this.productManagementService.searchProducts(request);
        this.paginator.firstPage();
      } catch {
      } finally {
        loadingRef.closeLoading();
      }
    }
  }

  filtersValidate(): boolean {
    if (this.startAt || this.endAt) {
      const startAt = moment(this.startAt, "DD/MM/YYYY");
      const endAt = moment(this.endAt, "DD/MM/YYYY");

      if (!this.startAt || !this.endAt) {
        this.toast.showWarningToast("Para filtrar por data, é necessário preencher o intervalo completo.");
        return false;
      }
      if (startAt.isAfter(endAt)) {
        this.toast.showWarningToast("Data inicial não pode ser maior que data final.");
        return false;
      }
      if (!startAt.isValid()) {
        this.toast.showWarningToast("Atenção! Data inicial inválida.");
        return false;
      }
      if (!endAt.isValid()) {
        this.toast.showWarningToast("Atenção! Data final inválida.");
        return false;
      }
    }

    return true;
  }

  public async onPaginatorChange(event: PageEvent): Promise<void> {
    const loadingRef = this.loadingService.beginLoading();
    try {
      const request = new ProductManagementSearchRequest();
      request.setStartIndex(event.pageIndex * event.pageSize + 1);
      request.setTotalPerPages(event.pageSize);
      request.setFilterText(this.filterText);
      request.setPendingProducts(this.pendingProducts);
      request.setStartAt(this.startAt);
      request.setEndAt(this.endAt);
      this.productManagementSearchResult = await this.productManagementService.searchProducts(request);
    } catch {
    } finally {
      loadingRef.closeLoading();
    }
  }

  public hasAccessToEditProduct(): boolean {
    return this.userService.getLoggedUser().hasPermission(UserPermissions.EditProduct);
  }

  public hasAccessToInsertProduct(): boolean {
    return this.userService.getLoggedUser().hasPermission(UserPermissions.InsertProduct);
  }

  public selectDateType() {}
}
